import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Snackbar from '@mui/material/Snackbar';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { postTaricHistory } from '../../api/taric';
import { Button } from '../../components/Buttons';
import ContentPanel from '../../components/ContentPanel';
import isoDateConverter from '../../legacy-components/isoDateConverter';

const TaricToolHistory = () => {
  const { commodityCode } = useParams();
  const navigate = useNavigate();
  const [toast, setToast] = useState({ open: false, message: '' });

  const {
    data: historyData = [],
    error,
    isFetching,
  } = useQuery({
    queryKey: ['taricHistory', commodityCode],
    queryFn: () => postTaricHistory(commodityCode),
    enabled: !!commodityCode,
  });

  useEffect(() => {
    if (!error) return;

    setToast({
      open: true,
      message: `Failed to load history data: ${error.message}`,
    });
  }, [error]);

  const handleCloseToast = () => {
    setToast((prev) => ({ ...prev, open: false }));
  };

  return (
    <div className="full-width-container">
      <ContentPanel maxWidth={1200} sx={{ my: '2rem' }}>
        <Typography variant="h4" sx={{ color: '#033249', mb: 2, fontWeight: 500 }}>
          TARIC History
        </Typography>

        <Button
          variant="text"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate(`/taric-tool/${commodityCode}`)}
          sx={{ mb: 2, px: 0, py: 0.5, lineHeight: 1.25, textDecoration: 'underline' }}
        >
          Back to Taric Details
        </Button>

        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          {isFetching && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              Loading history data...
            </Typography>
          )}
          {!isFetching && historyData.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
              No history records found.
            </Typography>
          )}
          {!isFetching && historyData.length > 0 && (
            <Table size="small" sx={{ bgcolor: 'common.white' }}>
              <TableHead>
                <TableRow>
                  <TableCell>TYPE</TableCell>
                  <TableCell>History</TableCell>
                  <TableCell>Who</TableCell>
                  <TableCell>When</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {historyData.map((item, index) => (
                  <TableRow key={`${item.type ?? 'row'}-${item.When ?? index}-${index}`}>
                    <TableCell>{item.type || ''}</TableCell>
                    <TableCell>{item.History || ''}</TableCell>
                    <TableCell>{item.Who || ''}</TableCell>
                    <TableCell>{item.When ? isoDateConverter(item.When) : ''}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </Box>
      </ContentPanel>

      <Snackbar
        open={toast.open}
        autoHideDuration={5000}
        onClose={handleCloseToast}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={handleCloseToast} severity="error" variant="filled" sx={{ width: '100%' }}>
          {toast.message}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default TaricToolHistory;
